import {
  enableProdMode,
  importProvidersFrom,
  provideAppInitializer,
  ErrorHandler,
  inject,
} from "@angular/core";
import {bootstrapApplication} from "@angular/platform-browser";
import {provideAnimations} from "@angular/platform-browser/animations";
import {provideServiceWorker} from "@angular/service-worker";
import {
  provideHttpClient,
  withInterceptorsFromDi,
  HttpClient,
} from "@angular/common/http";
import {TranslateLoader, TranslateModule} from "@ngx-translate/core";
import * as Sentry from "@sentry/angular";

import {AppComponent} from "./app/app.component";
import {appRoutes} from "./app/app-routing.module";
import {environment} from "./environments/environment";

import {httpInterceptorProviders} from "./app/services/interceptors";
import {TranslateCacheLoader} from "./app/services/translateCacheLoader.service";
import {CacheService} from "./app/modules/shared/services/cache.service";
import {AppService} from "./app/services/app.service";
import {TranslationUpdaterService} from "./app/services/translationUpdater.service";
import {NotificationsService} from "./app/services/notifications.service";

import {
  PreloadAllModules,
  Router,
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
} from "@angular/router";
import {MAT_DATE_LOCALE, MatNativeDateModule} from "@angular/material/core";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {TranslationService} from "./app/services/translation.service";
import {PlatformService} from "./app/services/platform.service";
import {ScrollRestorationService} from "./app/services/scroll-restoration.service";
import {MatSelectCountryLangToken} from "select-country-angular-20";
import {MonacoEditorModule} from "ngx-monaco-editor-v2";
import {captureInitialRecoveryToken} from "./app/modules/auth/utils/recovery-token.util";
import {
  capturePublicWorkToken,
  redactWorkShareTelemetry,
} from "./app/modules/works/utils/public-work-token.util";

captureInitialRecoveryToken();
if (globalThis.location.pathname.startsWith("/work/")) {
  const publicPath = capturePublicWorkToken(new URL(globalThis.location.href));
  history.replaceState(history.state, "", publicPath);
}

if (environment.production) {
  enableProdMode();

  Sentry.init({
    dsn: "https://6c2e5b64ba9db3c000981e5326783117@o4508760208179200.ingest.de.sentry.io/4508760447909968",
    environment: "production",
    release: environment.appVersion,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        beforeAddRecordingEvent: (event) => redactWorkShareTelemetry(event),
      }),
      Sentry.consoleLoggingIntegration({levels: ["log", "warn", "error"]}),
    ],
    enableLogs: true,
    beforeSend: redactWorkShareTelemetry,
    beforeSendTransaction: redactWorkShareTelemetry,
    beforeBreadcrumb: redactWorkShareTelemetry,
    beforeSendLog: redactWorkShareTelemetry,
    tracesSampleRate: 0.2,
    tracePropagationTargets: [/^https:\/\/app.gemster.io/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
  Sentry.setTag("platform", PlatformService.getPlatform());
}

export function initializeSentry(
  _sentryTraceService: Sentry.TraceService
): () => void {
  return (): void => {};
}

const sentryProviders = environment.production
  ? [
      {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler(),
      },
      {
        provide: Sentry.TraceService,
        deps: [Router],
      },
      provideAppInitializer(() => {
        const initializerFn = initializeSentry(inject(Sentry.TraceService));
        return initializerFn();
      }),
    ]
  : [];

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(
      appRoutes,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({scrollPositionRestoration: "disabled"})
    ),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    {provide: MAT_DATE_LOCALE, useValue: "fr-FR"},
    // Http interceptors
    httpInterceptorProviders,
    // MatDate, forms, etc.
    importProvidersFrom(
      MatInputModule,
      MatFormFieldModule,
      MatDatepickerModule,
      MatNativeDateModule,
      MonacoEditorModule.forRoot({
        baseUrl: "/assets/monaco/min/vs",
        defaultOptions: {scrollBeyondLastLine: false},
      })
    ),
    // ngx-translate
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: (
            http: HttpClient,
            cache: CacheService,
            app: AppService
          ) => new TranslateCacheLoader(http, cache, app),
          deps: [HttpClient, CacheService, AppService],
        },
      })
    ),
    // Service worker
    provideServiceWorker("ngsw.js", {
      enabled: environment.production && !PlatformService.isNativePlatform(),
      registrationStrategy: "registerImmediately",
      updateViaCache: "none",
    }),
    // Tes services
    AppService,
    NotificationsService,
    TranslationUpdaterService,
    TranslationService,
    {provide: MAT_DATE_LOCALE, useValue: "fr-FR"},
    {provide: MatSelectCountryLangToken, useValue: "fr"},

    provideAppInitializer(() => {
      inject(ScrollRestorationService);
    }),

    provideAppInitializer(async () => {
      const cacheService = inject(CacheService);
      await cacheService.init();
    }),
    ...sentryProviders,
  ],
});
